import {
  APP_BOOTSTRAP_LISTENER,
  ApplicationRef,
  inject,
  makeEnvironmentProviders,
  NgZone,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';

import { LocalStorageService } from '@cosmos/data-access-storage';
import {
  ExplicitFeatureFlagsService,
  FEATURE_FLAGS_FROM_SESSION_DATA,
  FEATURE_FLAGS_OPTIONS,
  resetFeatureFlagOverridesOnGitShaMismatch,
  setPersistentFeatureFlags,
  type FeatureFlagsOptions,
  type FeatureFlagsSet,
} from '@cosmos/feature-flags';
import { LCP$ } from '@cosmos/tick-scheduler';
import { cloneDeep } from '@cosmos/util-common/clone-deep';
import { injectWindow } from '@cosmos/util-common/di';
import { merge } from '@cosmos/util-common/merge';

export function provideFeatureFlagsToolbarInitializer(
  options: FeatureFlagsOptions,
  ...providers: EnvironmentProviders[]
): EnvironmentProviders {
  return makeEnvironmentProviders([
    ...providers,
    { provide: FEATURE_FLAGS_OPTIONS, useValue: options },
    provideAppInitializer(() => {
      const featureFlagService = inject(ExplicitFeatureFlagsService);
      const localStorageService = inject(LocalStorageService);

      if (!ngServerMode) {
        resetFeatureFlagOverridesOnGitShaMismatch(
          localStorageService,
          process.env['GIT_SHA']
        );
      }

      const overrides = localStorageService.getItem<FeatureFlagsOptions>(
        ExplicitFeatureFlagsService.featureFlagOverridesStorageKey
      );
      const sessionOverrides =
        inject(FEATURE_FLAGS_FROM_SESSION_DATA, { optional: true }) ?? {};
      if (overrides) {
        options = merge(cloneDeep(options), overrides);
      }

      const featureFlag = featureFlagService.selectedFeatureFlagName;
      const option: FeatureFlagsSet | undefined = options?.[featureFlag];
      if (option) {
        if (sessionOverrides) {
          option.flags = merge(cloneDeep(option.flags), sessionOverrides);
        }
        featureFlagService.setFlags(option.flags);
      }
    }),
    {
      provide: APP_BOOTSTRAP_LISTENER,
      multi: true,
      useFactory: () => {
        if (ngServerMode) {
          return () => {};
        }

        if (global_isRealProduction) {
          // Allow debug toolbar if explicitly enabled via ?debug={githash}
          const win = injectWindow();
          const debugEnabled =
            win?.localStorage?.getItem('__debug_enabled') === 'true';
          if (!debugEnabled) {
            throw new Error('Should not be provided at the actual production.');
          }
        }

        const appRef = inject(ApplicationRef);
        const lcp$ = inject(LCP$);
        const ngZone = inject(NgZone);

        const win = injectWindow();
        const localStorageService = inject(LocalStorageService);
        (win as any).setFeatureFlags = (
          flags: Record<string, boolean>,
          options?: { reset?: boolean }
        ) =>
          setPersistentFeatureFlags(flags, {
            ...options,
            _window: win,
            _localStorageService: localStorageService,
          });

        return () => {
          // Wait for the app to fully bootstrap to avoid conflicts
          // with other frame drops in the performance tab.
          lcp$.subscribe(() => {
            import('./feature-flags-toolbar.component').then((m) => {
              ngZone.run(() => m.renderFeatureFlagsToolbar(appRef));
            });
          });
        };
      },
    },
  ]);
}
